import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const { t, i18n } = useTranslation();

  const isNorwegian = i18n.language.startsWith("nb") || i18n.language === "no";

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const navItems = [
    { labelKey: "nav.about", id: "about" },
    { labelKey: "nav.approach", id: "tagline" },
    { labelKey: "nav.contact", id: "contact" },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-[#F8F7F4]/95 backdrop-blur-sm border-b border-[#E8E4DC]" : "bg-transparent"
      }`}
    >
      <nav className="max-w-6xl mx-auto px-8 flex items-center justify-between h-20">
        {/* Logo */}
        <div className="flex items-center gap-3">
          <img
            src="https://public.readdy.ai/ai/img_res/393595e6-d208-41ce-a3f5-17096d7bbb19.png"
            alt="ExoMed logo"
            className="h-9 w-auto object-contain"
          />
        </div>

        {/* Right side */}
        <div className="hidden md:flex items-center gap-8">
          {/* Nav links */}
          <ul className="flex items-center gap-8">
            {navItems.map((item) => (
              <li key={item.id}>
                <button
                  onClick={() => scrollTo(item.id)}
                  className={`text-[13px] tracking-widest uppercase font-medium transition-colors duration-300 cursor-pointer whitespace-nowrap ${
                    scrolled
                      ? "text-[#4A4840] hover:text-[#3D6B5A]"
                      : "text-white/80 hover:text-white"
                  }`}
                  style={{ fontFamily: "'DM Sans', sans-serif" }}
                >
                  {t(item.labelKey)}
                </button>
              </li>
            ))}
          </ul>

          {/* Language toggle */}
          <div
            className={`flex items-center rounded-full p-0.5 border transition-all duration-500 ${
              scrolled ? "border-[#E8E4DC]" : "border-white/25"
            }`}
          >
            {(["en", "nb"] as const).map((lang) => {
              const isActive = lang === "nb" ? isNorwegian : !isNorwegian;
              return (
                <button
                  key={lang}
                  onClick={() => i18n.changeLanguage(lang)}
                  className={`px-3.5 py-1.5 rounded-full text-[11px] tracking-widest uppercase font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                    isActive
                      ? scrolled
                        ? "bg-[#3D6B5A] text-white"
                        : "bg-white/20 text-white"
                      : scrolled
                      ? "text-[#6B6B65] hover:text-[#3D6B5A]"
                      : "text-white/50 hover:text-white/85"
                  }`}
                  style={{ fontFamily: "'DM Sans', sans-serif" }}
                >
                  {lang === "en" ? "EN" : "NO"}
                </button>
              );
            })}
          </div>

          {/* CTA */}
          <a
            href="mailto:kontakt@exomed.no"
            className={`inline-flex items-center gap-2 px-5 py-2.5 text-[13px] tracking-widest uppercase font-medium rounded-md transition-colors duration-300 cursor-pointer whitespace-nowrap ${
              scrolled
                ? "bg-[#3D6B5A] text-white hover:bg-[#2F5547]"
                : "bg-white/15 text-white border border-white/30 hover:bg-white/25"
            }`}
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            {t("nav.getInTouch")}
          </a>
        </div>
      </nav>
    </header>
  );
}
